import { deepMerge } from '@empathybroker/deep-merge';
import { Store, StoreOptions } from 'vuex';
import { container } from '../container/container';
import { DEPENDENCIES } from '../container/dependencies.const';
import { ConfigState } from '../models';
import { StateCacheService } from '../services/state-cache.service';
import { Dictionary } from '../utils';
import { getConfigModule } from './config/config.module';
import { CORE_ACTIONS } from './core/core.actions';
import { CORE_MODULE } from './core/core.module';
import { CORE_MUTATIONS } from './core/core.mutations';
import { EMPATHIZE_ACTIONS } from './empathize/empathize.actions';
import { EMPATHIZE_GETTERS } from './empathize/empathize.getters';
import { EMPATHIZE_MODULE } from './empathize/empathize.module';
import { EMPATHIZE_MUTATIONS } from './empathize/empathize.mutations';
import { getEmptySearchModule } from './empty-search/empty-search.module';
import { HISTORY_ACTIONS } from './history/history.actions';
import { HISTORY_GETTERS } from './history/history.getters';
import { HISTORY_MODULE } from './history/history.module';
import { HISTORY_MUTATIONS } from './history/history.mutations';
import { getNoResultsModule } from './no-results/no-results.module';
import { QUERY_SIGNALS_ACTIONS } from './query-signals/query-signals.actions';
import { QUERY_SIGNALS_GETTERS } from './query-signals/query-signals.getters';
import { QUERY_SIGNALS_MODULE } from './query-signals/query-signals.module';
import { QUERY_SIGNALS_MUTATIONS } from './query-signals/query-signals.mutations';
import { SEARCH_ACTIONS } from './search/search.actions';
import { SEARCH_GETTERS } from './search/search.getters';
import { SEARCH_MODULE } from './search/search.module';
import { SEARCH_MUTATIONS } from './search/search.mutations';
import { SUBMODULES_PATHS } from './store.const';
import { RootStoreState } from './store.interfaces';
import { TRACKING_ACTIONS } from './tracking/tracking.actions';
import { TRACKING_MODULE } from './tracking/tracking.module';
import { TRACKING_MUTATIONS } from './tracking/tracking.mutations';
import { URL_ACTIONS } from './url/url.actions';
import { URL_GETTERS } from './url/url.getters';
import { URL_MODULE } from './url/url.module';
import { URL_MUTATIONS } from './url/url.mutations';

declare module 'vuex' {
  export function mapState<T extends keyof typeof SUBMODULES_PATHS>(module: T,
    props: (keyof RootStoreState[T])[] | Dictionary<keyof RootStoreState[T]>): any;
}

export function createStore(configState: ConfigState, storeOverrideOptions?: StoreOptions<RootStoreState>): Store<RootStoreState> {
  let storeOptions: StoreOptions<RootStoreState> = null;

  const hasUserBackFromPDP = container.get<() => boolean>(DEPENDENCIES.HasUserNavigatedBackFromPDP);
  const stateCacheService = container.get<StateCacheService>(DEPENDENCIES.StateCacheService);
  const cachedState = stateCacheService.getStateFromCache();

  if (cachedState && hasUserBackFromPDP()) {
    storeOptions = deepMerge(getStateStoreOptions(cachedState, configState), storeOverrideOptions);
    stateCacheService.deleteStateFromCache();
  } else {
    storeOptions = deepMerge(getDefaultStoreOptions(configState), storeOverrideOptions);
  }

  return new Store(storeOptions);
}

function getDefaultStoreOptions(configState: ConfigState): StoreOptions<RootStoreState> {
  const activeConfig = configState.config[configState.device];
  return {
    modules: {
      [SUBMODULES_PATHS.config]: getConfigModule(configState),
      [SUBMODULES_PATHS.core]: CORE_MODULE,
      [SUBMODULES_PATHS.empathize]: EMPATHIZE_MODULE,
      [SUBMODULES_PATHS.emptySearch]: getEmptySearchModule(activeConfig),
      [SUBMODULES_PATHS.history]: HISTORY_MODULE,
      [SUBMODULES_PATHS.noResults]: getNoResultsModule(activeConfig),
      [SUBMODULES_PATHS.querySignals]: QUERY_SIGNALS_MODULE,
      [SUBMODULES_PATHS.search]: SEARCH_MODULE,
      [SUBMODULES_PATHS.tracking]: TRACKING_MODULE,
      [SUBMODULES_PATHS.url]: URL_MODULE
    },
    strict: process.env.NODE_ENV !== 'production'
  };
}

function getStateStoreOptions(cachedState: RootStoreState, configState: ConfigState): StoreOptions<RootStoreState> {
  const activeConfig = configState.config[configState.device];
  return {
    modules: {
      [SUBMODULES_PATHS.config]: getConfigModule(configState),
      [SUBMODULES_PATHS.core]: {
        namespaced: true,
        actions: CORE_ACTIONS,
        mutations: CORE_MUTATIONS,
        state: deepMerge(cachedState.core, { stateLoadedFromCache: true })
      },
      [SUBMODULES_PATHS.empathize]: {
        namespaced: true,
        actions: EMPATHIZE_ACTIONS,
        mutations: EMPATHIZE_MUTATIONS,
        getters: EMPATHIZE_GETTERS,
        state: cachedState.empathize
      },
      [SUBMODULES_PATHS.history]: {
        namespaced: true,
        actions: HISTORY_ACTIONS,
        mutations: HISTORY_MUTATIONS,
        getters: HISTORY_GETTERS,
        state: cachedState.history
      },
      [SUBMODULES_PATHS.querySignals]: {
        namespaced: true,
        actions: QUERY_SIGNALS_ACTIONS,
        mutations: QUERY_SIGNALS_MUTATIONS,
        getters: QUERY_SIGNALS_GETTERS,
        state: cachedState.querySignals
      },
      [SUBMODULES_PATHS.search]: {
        namespaced: true,
        actions: SEARCH_ACTIONS,
        mutations: SEARCH_MUTATIONS,
        getters: SEARCH_GETTERS,
        state: cachedState.search
      },
      [SUBMODULES_PATHS.tracking]: {
        namespaced: true,
        actions: TRACKING_ACTIONS,
        mutations: TRACKING_MUTATIONS,
        state: cachedState.tracking
      },
      [SUBMODULES_PATHS.url]: {
        namespaced: true,
        actions: URL_ACTIONS,
        mutations: URL_MUTATIONS,
        getters: URL_GETTERS,
        state: cachedState.url
      },
      [SUBMODULES_PATHS.noResults]: deepMerge(getNoResultsModule(activeConfig), {
        modules: {
          recommendations: {
            state: cachedState.noResults.recommendations
          }
        }
      }),
      [SUBMODULES_PATHS.emptySearch]: deepMerge(getEmptySearchModule(activeConfig), {
        modules: {
          recommendations: {
            state: cachedState.emptySearch.recommendations
          },
          discoveryWall: {
            state: cachedState.emptySearch.discoveryWall
          }
        }
      })
    },
    strict: process.env.NODE_ENV !== 'production'
  };
}
